// The information pages a payment provider (Razorpay) checks before it takes payments for the site: About us,
// Contact us, Pricing, Terms and conditions, Privacy policy, Cancellation and refunds, Shipping and delivery. The
// business details come from BUSINESS in brand.ts (shown highlighted while unfilled); the prices from REG and PASS.
// The policies are a plain-language starting point: have them checked against your own business before relying on them.
import { useEffect, useRef } from 'react';
import { BUSINESS, PASS, REG, SITE_NAME, unfilled } from './brand';
import { Fill, SiteFooter, SiteNav } from './Chrome';
import { openRegister } from './Register';
import { linkProps } from './router';
import './home.css';

type Page = { title: string; kicker: string; body: () => React.ReactNode };

const B = BUSINESS;
const mail = <a href={`mailto:${B.email}`}>{B.email}</a>;
const Updated = () => <p className="gx-upd mono">Last updated: {B.policiesUpdated}</p>;
const RegButton = () => (
  <p>
    <button type="button" className="gx-btn" onClick={() => openRegister()}>
      Join the waitlist · ₹{REG.fee} <span aria-hidden>→</span>
    </button>
  </p>
);

export const PAGES: Record<string, Page> = {
  '/about': {
    kicker: 'About us',
    title: 'Who we are',
    body: () => (
      <>
        <p>
          {SITE_NAME} is an independent studio from Chandigarh, run by <Fill v={B.legalName} />. We build India’s worlds in 3D: real Indian cities rebuilt street by
          street as open worlds you can play in a web browser (no download, on a phone or a laptop), and live shows and events inside them.
        </p>
        <p>
          Our first game is <b>Chandigarh: The City Beautiful</b>, the city at 1:1, built from public map data and hundreds of photographs. Walk Sector 17, drive
          Madhya Marg, fly a drone over Sukhna Lake and meet your friends on the Plaza, with text and voice chat. It is in a closed beta now.
        </p>
        <h2>What we sell</h2>
        <ul>
          <li>
            <b>Name registration, ₹{REG.fee} (now):</b> reserves your unique GG Bharat name (the one you use in all our games, shows and events) and your place in
            line for when the game opens.
          </li>
          <li>
            <b>The {PASS.name}, ₹{PASS.price} (at launch):</b> lifetime access to the game.
          </li>
        </ul>
        <p>
          Both are digital, paid once, taxes included (see <a {...linkProps('/pricing')}>Pricing</a>). We don’t sell physical goods.
        </p>
        <h2>Not affiliated</h2>
        <p>
          {SITE_NAME} and its games are not run by, endorsed by or linked to the Chandigarh Administration, the Chandigarh Traffic Police or any government body.
          Anything in a game that looks official (a traffic camera, a challan) is part of the game and not real.
        </p>
      </>
    ),
  },
  '/contact': {
    kicker: 'Contact us',
    title: 'Get in touch',
    body: () => (
      <>
        <p>Questions about the game, your name, a payment or a refund? Write to us, and we answer within two working days.</p>
        <dl className="gx-dl">
          <div>
            <dt>Email</dt>
            <dd>{mail}</dd>
          </div>
          {!unfilled(B.phone) && (
            <div>
              <dt>Phone</dt>
              <dd>{B.phone}</dd>
            </div>
          )}
          <div>
            <dt>Hours</dt>
            <dd>{B.hours}</dd>
          </div>
          <div>
            <dt>Business</dt>
            <dd>
              <Fill v={B.legalName} /> ({SITE_NAME})
            </dd>
          </div>
          <div>
            <dt>Address</dt>
            <dd>
              <Fill v={B.address} />
            </dd>
          </div>
        </dl>
        <p>For a refund, write from the email you registered with, with your in-game name and the payment’s date (or its Razorpay payment ID).</p>
      </>
    ),
  },
  '/pricing': {
    kicker: 'Pricing',
    title: 'Two prices. Both one-time.',
    body: () => (
      <>
        <div className="gx-receipts page">
          <div className="gx-receipt">
            <div className="r-head mono">
              <b>NAME REGISTRATION</b>
              <span>NOW</span>
            </div>
            <p className="amt">
              <span>₹</span>
              {REG.fee}
            </p>
            <ul className="mono">
              <li>Your unique GG Bharat name, reserved for good</li>
              <li>A founder number, in order of registration</li>
              <li>First in when the game opens</li>
              <li>{REG.refundDays}-day full refund</li>
            </ul>
            <p className="t-fine mono">One-time · taxes included</p>
          </div>
          <div className="gx-receipt alt">
            <div className="r-head mono">
              <b>{PASS.name.toUpperCase()}</b>
              <span>AT LAUNCH</span>
            </div>
            <p className="amt">
              <span>₹</span>
              {PASS.price}
            </p>
            <ul className="mono">
              {PASS.includes.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
            <p className="t-fine mono">One-time · taxes included · not on sale yet</p>
          </div>
        </div>
        <ul className="gx-facts">
          <li>
            <b>Name registration (₹{REG.fee}):</b> a one-time digital service that reserves the name you choose, unique across {SITE_NAME}’s games, shows and events, for your use. Delivered
            instantly (see <a {...linkProps('/delivery')}>Shipping & delivery</a>).
          </li>
          <li>
            <b>{PASS.name} (₹{PASS.price}):</b> a {PASS.kind}. On sale when the game opens to everyone; nothing is charged for it now.
          </li>
          <li>
            <b>Paying:</b> by UPI, cards, net banking or wallets through Razorpay, in Indian rupees (₹). We never see or store your card or bank details.
          </li>
          <li>
            <b>Refunds:</b> ask within {REG.refundDays} days of paying for your money back (see <a {...linkProps('/refunds')}>Cancellation & refunds</a>).
          </li>
        </ul>
        <RegButton />
      </>
    ),
  },
  '/terms': {
    kicker: 'Terms & conditions',
    title: 'Terms of use',
    body: () => (
      <>
        <Updated />
        <p>
          These terms are an agreement between you and <Fill v={B.legalName} /> (“we”, “{SITE_NAME}”) for using ggbharat.com, Chandigarh: The City Beautiful and
          our other games, shows and events (“the service”). By using the service you accept them.
        </p>
        <h2>1. Who can use it</h2>
        <p>You must be at least 13. If you’re under 18, a parent or guardian must agree to these terms for you and make any payment.</p>
        <h2>2. Your name and your account</h2>
        <p>
          A registered GG Bharat name is unique: one name a person, matched to the email and phone you register with. It’s for your own use and can’t be sold or
          transferred. Names that impersonate someone, a brand or an authority, or that are offensive, may be refused or taken back; if we take one back for that
          reason, we refund its fee. Keep your password safe and your email up to date; you’re responsible for what happens on your account.
        </p>
        <h2>3. Playing fair</h2>
        <p>
          Don’t harass, threaten or abuse other players; don’t post hateful, sexual or illegal content in names, chat or voice; don’t cheat, attack, overload or
          reverse-engineer the service. We may mute, suspend or close accounts that break these rules.
        </p>
        <h2>4. Payments</h2>
        <p>
          Prices are in Indian rupees and include taxes. The name registration (₹{REG.fee}) reserves a name; it doesn’t include the {PASS.name}, which goes on sale
          separately when the game opens. A pass gives you a personal, non-transferable licence to play, not ownership of the game. Payments are processed by
          Razorpay; their terms apply to the payment itself. Refunds follow our <a {...linkProps('/refunds')}>refund policy</a>.
        </p>
        <h2>5. The game world</h2>
        <p>
          Our games are fiction set in real places. Places are modelled from public map data and photographs; heights and some layouts are estimates. Anything
          official-looking in a game, like a traffic camera or a “challan”, is part of the game: it is not real, carries no legal effect and is not linked to any
          authority. Don’t try anything from a game on real roads.
        </p>
        <h2>6. Our content</h2>
        <p>The games, code, art and brand are ours or used under licence. Map data © OpenStreetMap contributors, under the ODbL. Don’t copy or resell them.</p>
        <h2>7. The service as it is</h2>
        <p>
          We work to keep the service running, but it may change, pause or have bugs, especially during the beta. To the extent the law allows, the service is provided
          “as is”, and our total liability to you is limited to what you paid us in the last 12 months.
        </p>
        <h2>8. Changes and law</h2>
        <p>
          We may update these terms; we’ll show the new date here, and tell registered players by email about big changes. These terms are governed by the laws of
          India, and the courts of {B.jurisdiction} have jurisdiction.
        </p>
        <h2>9. Contact</h2>
        <p>Questions about these terms: {mail}.</p>
      </>
    ),
  },
  '/privacy': {
    kicker: 'Privacy policy',
    title: 'Your privacy',
    body: () => (
      <>
        <Updated />
        <p>
          This policy explains what personal data <Fill v={B.legalName} /> ({SITE_NAME}) collects on ggbharat.com and in our games, why, and your rights, under the
          laws of India including the Digital Personal Data Protection Act, 2023.
        </p>
        <h2>What we collect</h2>
        <ul>
          <li>
            <b>When you register a name:</b> the name, your email, your mobile number, and your state and city.
          </li>
          <li>
            <b>Account:</b> your email, display name, optional Instagram handle, and your password (stored only as a one-way hash; we can’t read it).
          </li>
          <li>
            <b>While you play:</b> your position and movements are passed live to other players in your room and not stored. The last 30 chat messages in a room are
            kept in the server’s memory, not saved to disk. Voice chat goes directly between players’ browsers; we don’t record it.
          </li>
          <li>
            <b>Payments:</b> Razorpay collects your payment details. We receive only a confirmation (payment ID, amount, status), never your card or bank details.
          </li>
          <li>
            <b>Technical:</b> your IP address, used briefly to stop abuse (rate limits), and anonymous visit statistics from Cloudflare Web Analytics (no cookies).
          </li>
        </ul>
        <h2>Why</h2>
        <p>
          To reserve your name and contact you about it (your receipt, and the day the game opens), to understand where our players are from so we choose which city to
          build next, to run your account and the game, to keep the service safe, and to meet legal duties. We don’t sell your data, and we don’t use it for
          advertising. Your email and phone are never shown to other players.
        </p>
        <h2>Cookies and storage</h2>
        <p>One cookie keeps you logged in (for 30 days). Your browser’s local storage keeps your game settings, where you last were in the game, and your registered name. Nothing else tracks you.</p>
        <h2>Who else handles it</h2>
        <p>
          Our service providers, only to run the service: Cloudflare (website delivery and security), Oracle Cloud (our server, in Mumbai), Google (a Google Sheet
          where we keep the registrations list), Razorpay (payments), an email provider (Resend) for account emails, and Google Fonts. In the game, YouTube videos play
          through YouTube’s own player.
        </p>
        <h2>How long</h2>
        <p>Registrations as long as the name is yours, or until you ask us to remove them; accounts until you delete them; payment records as long as tax law requires.</p>
        <h2>Your rights</h2>
        <p>
          You can ask to see, correct or delete your data, withdraw consent, or complain. Email {mail}. Grievance officer: <Fill v={B.legalName} />, at the same
          address; we reply within 7 days and resolve within 30.
        </p>
        <h2>Children</h2>
        <p>The service is for people aged 13 and over. Under-18s need a parent or guardian’s consent, and we don’t knowingly collect more than a registration needs from them.</p>
      </>
    ),
  },
  '/refunds': {
    kicker: 'Cancellation & refunds',
    title: 'Cancellation and refunds',
    body: () => (
      <>
        <Updated />
        <ul>
          <li>
            <b>Name registration (₹{REG.fee}):</b> changed your mind? Ask within {REG.refundDays} days of paying and we refund the full ₹{REG.fee}, no questions asked. The
            name is then released for others.
          </li>
          <li>
            <b>Name taken while you paid:</b> if a payment arrives after the name you chose was taken (rare), we refund it in full or let you pick another name, as you
            prefer.
          </li>
          <li>
            <b>{PASS.name} (at launch):</b> ask within {PASS.refundDays} days of buying for a full refund; and if the game doesn’t run on your device and we can’t fix it,
            we refund you even after that.
          </li>
          <li>
            <b>Double or failed payments:</b> if you were charged twice, or charged without getting what you paid for, we refund the extra or failed payment in full.
          </li>
          <li>
            <b>How:</b> email {mail} from the email you registered with, with your in-game name and the payment date or Razorpay payment ID. We confirm within 2 working
            days.
          </li>
          <li>
            <b>When the money arrives:</b> refunds go back to the original payment method through Razorpay, usually within 5–7 working days of approval (your bank may
            take longer).
          </li>
          <li>
            <b>Cancelling:</b> everything is a one-time payment, so there’s no subscription to cancel.
          </li>
          <li>
            <b>No refund</b> for names or accounts taken back for breaking the <a {...linkProps('/terms')}>terms</a>, except offensive or impersonating names (refunded),
            or where the law requires one.
          </li>
        </ul>
      </>
    ),
  },
  '/delivery': {
    kicker: 'Shipping & delivery',
    title: 'Shipping and delivery',
    body: () => (
      <>
        <Updated />
        <ul>
          <li>
            <b>Digital only:</b> everything we sell is a digital service: nothing physical is shipped, and there are no shipping charges.
          </li>
          <li>
            <b>Name registration:</b> delivered the moment the payment succeeds: your name is reserved, and your founder certificate appears on screen to download. Razorpay
            emails you the payment receipt.
          </li>
          <li>
            <b>{PASS.name} (at launch):</b> access is added to your {SITE_NAME} account as soon as the payment succeeds, usually within minutes.
          </li>
          <li>
            <b>Where:</b> available across India. You play at ggbharat.com on any modern browser; nothing to download or install.
          </li>
          <li>
            <b>Not delivered?</b> If you paid and don’t see your registration within 24 hours, write to {mail} with the payment ID and we sort it out or refund you.
          </li>
        </ul>
      </>
    ),
  },
};

export function InfoPage({ path }: { path: string }) {
  const page = PAGES[path];
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    document.title = `${page.kicker} · ${SITE_NAME}`;
    root.current?.scrollTo({ top: 0 });
  }, [page]);
  return (
    <div className="gx" ref={root}>
      <div className="gx-noise" aria-hidden />
      <SiteNav scroller={root} />
      <main className="gx-info">
        <p className="gx-kicker mono">{page.kicker}</p>
        <h1>{page.title}</h1>
        <div className="gx-prose">{page.body()}</div>
      </main>
      <SiteFooter />
    </div>
  );
}
